import React from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { ExecutorDashboard } from './dashboard/ExecutorDashboard';
import { BusinessDashboard } from './dashboard/BusinessDashboard';
import { ManagerDashboardPage } from './dashboard/ManagerDashboardPage';
import { PlatformCuratorDashboardPage } from './dashboard/PlatformCuratorDashboardPage';
import AdminDashboardPage from './AdminDashboardPage'; 
import { EducationDashboardPage } from './dashboard/EducationDashboardPage'; 
import { useAuth } from '../contexts/AuthContext';
import { UserType, ManagerRole } from '../types/auth';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { AuthUser } from '../types/auth'; // Импортируем AuthUser

interface DashboardPageProps {
  // Если вам нужны какие-либо пропсы для DashboardPage, добавьте их здесь
}

export const DashboardPage: React.FC<DashboardPageProps> = () => {
  const { state } = useAuth();
  const navigate = useNavigate();

  // Показываем загрузку пока проверяется авторизация
  if (state.isLoading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '100vh',
          gap: 2
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          Загрузка...
        </Typography>
      </Box>
    );
  }

  // Если пользователь не авторизован, перенаправляем на страницу авторизации
  // Эта проверка теперь будет внутри renderDashboardContent

  const renderDashboardContent = () => {
    if (!state.user) {
      return (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Вы не авторизованы для доступа к этой странице.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/auth')} sx={{ mt: 2 }}>
            Перейти на страницу входа
          </Button>
        </Box>
      );
    }

    const user: AuthUser = state.user; // user теперь гарантированно не null

    // Дэшборд для исполнителя
    if (user.type === UserType.USER && user.user) {
      return <ExecutorDashboard />;
    }

    // Дэшборды для менеджеров в зависимости от роли
    if (user.type === UserType.MANAGER && user.manager) {
      const roles = user.manager.roles;

      // Куратор платформы
      if (roles.includes(ManagerRole.PLATFORM_CURATOR)) {
        return <PlatformCuratorDashboardPage />;
      }

      // Администратор
      if (roles.includes(ManagerRole.ADMIN)) {
        return <AdminDashboardPage />;
      }

      // Бизнес-заказчик (если ManagerRole.BUSINESS обозначает этот тип менеджера)
      if (roles.includes(ManagerRole.BUSINESS)) {
        return <BusinessDashboard />;
      }

      // Образовательное учреждение (если ManagerRole.EDUCATION обозначает этот тип менеджера)
      if (roles.includes(ManagerRole.EDUCATION)) {
        return <EducationDashboardPage />;
      }
      
      // Менеджер организации
      if (roles.includes(ManagerRole.ORGANIZATION_MANAGER)) {
        return <ManagerDashboardPage />;
      }
      
      // Куратор (общий, если есть, и не покрыт выше)
      if (roles.includes(ManagerRole.CURATOR)) {
        // Если у вас есть отдельный дашборд для общего куратора, используйте его здесь
        // Пока используем BusinessDashboard как заглушку
        return <BusinessDashboard />;
      }

      // Fallback для менеджеров без определенной роли (если ManagerDashboardPage не был покрыт выше)
      return <ManagerDashboardPage />;
    }
    
    // Fallback для всех остальных случаев, если пользователь авторизован, но его тип не соответствует ни одному дашборду
    return (
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Ваш тип пользователя не имеет привязанного дэшборда.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/auth')} sx={{ mt: 2 }}>
          Повторить вход
        </Button>
      </Box>
    );
  };

  return (
    <DashboardLayout>
      {renderDashboardContent()}
    </DashboardLayout>
  );
};